import { DOCS_LINKS } from "@hex/common";
import React, { ReactNode } from "react";

import { HexNonIdealState } from "../../hex-components";
import { LockIcon, WarningIcon } from "../icons/CustomIcons";

import { DocsLink } from "./DocsLink.js";

export type ErrorType =
  | "publish"
  | "authentication"
  | "authorization"
  | "creation"
  | "asset-access"
  | "other"
  | "session-not-viewable"
  | "session-not-viewable-embedded"
  | "not-found"
  | "expired-oauth";
export type ResourceType = "app" | "project" | "component" | "exploration";

export interface ErrorPageProps {
  readonly errorType: ErrorType;
  readonly resourceType: ResourceType;
  readonly action?: ReactNode;
}

export const ErrorPage: React.FunctionComponent<ErrorPageProps> = (props) => {
  const { action, errorType, resourceType } = props;
  switch (errorType) {
    case "publish":
      return (
        <HexNonIdealState
          action={<>{action}</>}
          description={`This ${resourceType} has been shared with you, but the author has not published a public version yet.`}
          icon={<LockIcon />}
          title={`This ${resourceType} has not been published`}
        />
      );
    case "asset-access":
      return (
        <HexNonIdealState
          action={<>{action}</>}
          description={
            <>
              This {resourceType} has been shared with you, but it uses data
              that you do not have permission to view.{" "}
              <DocsLink to={DOCS_LINKS.SharedAssetRoleDowngradeToNoAccess}>
                Learn more
              </DocsLink>
              .
            </>
          }
          icon={<LockIcon />}
          title={`Access to this ${resourceType} has been restricted`}
        />
      );
    case "creation":
      return (
        <HexNonIdealState
          action={<>{action}</>}
          description={`We could not create your ${resourceType}. Please contact support@hex.tech for assistance.`}
          icon="heart-broken"
          title="Something went wrong..."
        />
      );
    case "authentication":
      return (
        <HexNonIdealState
          action={<>{action}</>}
          icon={<LockIcon />}
          title={`You don't have access to this ${resourceType}`}
        />
      );
    case "authorization":
      return (
        <HexNonIdealState
          action={<>{action}</>}
          description={`The creator of this ${resourceType} has not shared it with you. They can grant you access using the Share dialog in their editor view.`}
          icon={<LockIcon />}
          title={`You don't have access to this ${resourceType}`}
        />
      );
    case "session-not-viewable":
      return (
        <HexNonIdealState
          action={<>{action}</>}
          description="If this should be a collaborative session, reach out to the session owner to make it collaborative."
          icon={<LockIcon />}
          title="You don't have access to this session"
        />
      );
    case "session-not-viewable-embedded":
      return (
        <HexNonIdealState
          action={<>{action}</>}
          description="The embedded app link is invalid. Contact your site administrator for assistance."
          icon={<LockIcon />}
          title="You don't have access to this session"
        />
      );
    case "not-found":
      return (
        <HexNonIdealState
          action={<>{action}</>}
          description={`This ${resourceType} was not found. If this ${resourceType} does exist, please contact support@hex.tech for assistance.`}
          icon={<WarningIcon />}
          title={`This ${resourceType} was not found.`}
        />
      );
    case "expired-oauth":
      return <HexNonIdealState action={<>{action}</>} />;
    case "other":
    default:
      return (
        <HexNonIdealState
          action={<>{action}</>}
          description={`There was an error loading this ${resourceType}. Please contact support@hex.tech for assistance.`}
          icon={<WarningIcon />}
          title={`Error loading ${resourceType}`}
        />
      );
  }
};
